'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  role: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User | null>;
  signUp: (email: string, password: string, metadata?: any) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isRetryableAuthFetchError(error: any) {
  const name = String(error?.name ?? '');
  const message = String(error?.message ?? '').toLowerCase();
  const causeCode = String(error?.cause?.code ?? '');
  return (
    name === 'AuthRetryableFetchError' ||
    message.includes('fetch failed') ||
    causeCode === 'UND_ERR_CONNECT_TIMEOUT'
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = useMemo(() => createClient(), []);

  const clearLocalAuthArtifacts = () => {
    if (typeof window === 'undefined') return;
    try {
      const shouldClearKey = (key: string) =>
        key.includes('auth-token') || (key.startsWith('sb-') && key.includes('-auth-token'));

      Object.keys(window.localStorage)
        .filter(shouldClearKey)
        .forEach((key) => window.localStorage.removeItem(key));

      Object.keys(window.sessionStorage)
        .filter(shouldClearKey)
        .forEach((key) => window.sessionStorage.removeItem(key));

      // Best-effort cleanup of non-HttpOnly auth cookies in case stale tokens linger.
      const cookieNames = document.cookie
        .split(';')
        .map((entry) => entry.split('=')[0]?.trim())
        .filter((name) => Boolean(name))
        .filter((name) => name.startsWith('sb-') || name.includes('auth-token'));
      cookieNames.forEach((name) => {
        document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Strict`;
      });
    } catch (storageError) {
      console.warn('AuthContext: failed to clear local auth artifacts:', storageError);
    }
  };

  const syncServerSession = async (accessToken: string, refreshToken: string) => {
    try {
      await fetch('/api/v1/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ accessToken, refreshToken }),
      });
      // Poll until the server confirms the cookie is present (max 3s).
      for (let i = 0; i < 6; i++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        try {
          const check = await fetch('/api/v1/auth/session', {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
          });
          const json = await check.json().catch(() => ({}));
          if (json?.sessionCookiePresent) break;
        } catch {
          // continue polling
        }
      }
    } catch {
      // Non-fatal: Supabase SSR middleware will attempt cookie refresh on next request.
    }
  };

  const clearServerSession = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        signal: controller.signal,
      });
    } catch (logoutError) {
      console.warn('AuthContext: server logout warning:', logoutError);
    } finally {
      clearTimeout(timer);
    }
  };

  const bestEffortGlobalSignOut = async () => {
    try {
      await Promise.race([
        supabase.auth.signOut({ scope: 'global' }),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);
    } catch (signOutError) {
      console.warn('AuthContext: global signOut warning:', signOutError);
    }
  };

  const resolveUserRole = async (currentUser: User | null): Promise<string | null> => {
    if (!currentUser) return null;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', currentUser.id)
        .maybeSingle();

      if (error) {
        console.warn('AuthContext: failed to resolve role from profile:', error.message);
      } else if (data?.role) {
        return String(data.role).toLowerCase();
      }
    } catch (roleError) {
      console.warn('AuthContext: unexpected role lookup error:', roleError);
    }

    const metadataRole = String(currentUser.user_metadata?.role ?? '')
      .toLowerCase()
      .trim();
    return metadataRole || null;
  };

  /** Periodically re-validate the session against the server.
   *  If the session was revoked server-side (e.g., global sign-out from another
   *  location), this detects it and force-clears local state — preventing stale
   *  session reuse (POPIA violation).
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const heartbeat = async () => {
      try {
        const {
          data: { user: freshUser },
          error,
        } = await supabase.auth.getUser();
        if (error || !freshUser) {
          // Session no longer valid on Supabase Auth server — force local clear.
          clearLocalAuthArtifacts();
          setUser(null);
          setRole(null);
        }
      } catch {
        // Network error; skip this heartbeat.
      }
    };
    // Run heartbeat 3s after mount and then every 5 minutes
    const initialTimer = window.setTimeout(() => void heartbeat(), 3000);
    const intervalHandle = setInterval(heartbeat, 5 * 60 * 1000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalHandle);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const applySessionState = async (session: Session | null) => {
      if (!isMounted) return;
      const sessionUser = session?.user ?? null;
      let resolvedUser = sessionUser;

      if (sessionUser) {
        try {
          const {
            data: { user: latestUser },
            error: latestUserError,
          } = await supabase.auth.getUser();
          if (latestUserError || !latestUser) {
            // Server says this session is invalid: force clear.
            clearLocalAuthArtifacts();
            if (isMounted) {
              setUser(null);
              setRole(null);
              setLoading(false);
            }
            return;
          }
          resolvedUser = latestUser;
        } catch (latestUserLookupError) {
          console.warn('AuthContext: failed to fetch latest auth user:', latestUserLookupError);
        }
      }

      setUser(resolvedUser);
      const resolvedRole = await resolveUserRole(resolvedUser);
      if (!isMounted) return;
      setRole(resolvedRole);
      setLoading(false);
    };

    const bootstrap = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      // On initial bootstrap, validate the session server-side.
      if (session?.access_token) {
        const {
          data: { user: freshUser },
          error,
        } = await supabase.auth.getUser();
        if (error || !freshUser) {
          // Stored session is invalid — clear everything.
          clearLocalAuthArtifacts();
          if (isMounted) {
            setUser(null);
            setRole(null);
            setLoading(false);
          }
          return;
        }
        await applySessionState({ ...session, user: freshUser });
      } else {
        await applySessionState(session);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      async (_event: AuthChangeEvent, session: Session | null) => {
        try {
          await applySessionState(session);
        } catch (authStateError) {
          console.warn('AuthContext: auth state change warning:', authStateError);
          if (isMounted) {
            setUser(session?.user ?? null);
            setRole(String(session?.user?.user_metadata?.role ?? '').toLowerCase() || null);
            setLoading(false);
          }
        }
      }
    );

    void bootstrap().catch((bootstrapError) => {
      console.warn('AuthContext: bootstrap warning:', bootstrapError);
      if (isMounted) {
        setUser(null);
        setRole(null);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [supabase]);

  const signIn = async (email: string, password: string) => {
    console.log('AuthContext: Attempting signIn...');
    const normalizedEmail = String(email ?? '')
      .trim()
      .toLowerCase();
    const normalizedPassword = String(password ?? '').trim();

    // Clear the current browser session before a role/account switch.
    // Use best-effort local sign-out so we do not revoke OTHER devices' sessions
    // when a user is merely switching accounts on this browser.
    try {
      await bestEffortGlobalSignOut();
      await clearServerSession();
      clearLocalAuthArtifacts();
    } catch (preSignInClearError) {
      console.warn('AuthContext: pre-signin session clear warning:', preSignInClearError);
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: normalizedPassword,
      });

      if (error) {
        console.error('AuthContext: signIn error:', error);
        throw error;
      }

      console.log('AuthContext: signIn successful:', data.user?.id);
      // Sync tokens to server-side HttpOnly cookies so API routes work
      // for all users regardless of network (mobile, external, incognito).
      if (data.session?.access_token && data.session?.refresh_token) {
        await syncServerSession(data.session.access_token, data.session.refresh_token);
      }
      const resolvedRole = await resolveUserRole(data.user ?? null);
      setUser(data.user ?? null);
      setRole(resolvedRole);
      setLoading(false);
      return data.user ?? null;
    } catch (error: any) {
      if (!isRetryableAuthFetchError(error)) {
        throw error;
      }

      console.warn('AuthContext: retryable browser auth failure, falling back to server login.');
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: normalizedEmail,
          password: normalizedPassword,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.error || 'Unable to contact the authentication service. Please try again.'
        );
      }

      const accessToken = String(payload?.accessToken ?? '');
      const refreshToken = String(payload?.refreshToken ?? '');
      if (!accessToken || !refreshToken) {
        throw new Error('Authentication fallback did not return a valid session.');
      }

      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (sessionError) {
        throw sessionError;
      }

      const signedInUser = sessionData?.user ?? null;
      if (sessionData?.session?.access_token && sessionData?.session?.refresh_token) {
        await syncServerSession(
          sessionData.session.access_token,
          sessionData.session.refresh_token
        );
      }
      const resolvedRole =
        String(payload?.user?.role ?? '')
          .toLowerCase()
          .trim() || (await resolveUserRole(signedInUser));
      setUser(signedInUser);
      setRole(resolvedRole || null);
      setLoading(false);
      return signedInUser;
    }
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    console.log('AuthContext: Attempting signUp...');
    const normalizedMetadata = {
      ...metadata,
      role: metadata?.role ?? 'customer',
      acquisition_channel: metadata?.acquisition_channel ?? 'web',
      primary_access_channel:
        metadata?.primary_access_channel ?? metadata?.acquisition_channel ?? 'web',
      consumer_segment: metadata?.consumer_segment ?? 'unknown',
      popia_consent_at:
        metadata?.popia_consent_at ??
        (metadata?.popia_consent === false ? null : new Date().toISOString()),
      marketing_consent: Boolean(metadata?.marketing_consent ?? false),
    };
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: normalizedMetadata },
    });

    if (error) {
      console.error('AuthContext: signUp error:', error);
      throw error;
    }

    console.log('AuthContext: signUp successful:', data.user?.id);
    if (data.user?.id) {
      try {
        await supabase.from('user_profiles').upsert({
          id: data.user.id,
          email,
          full_name: String(
            normalizedMetadata.full_name ??
              normalizedMetadata.name ??
              data.user.user_metadata?.full_name ??
              email.split('@')[0]
          ),
          phone: String(normalizedMetadata.phone ?? ''),
          role: normalizedMetadata.role,
          acquisition_channel: normalizedMetadata.acquisition_channel,
          primary_access_channel: normalizedMetadata.primary_access_channel,
          consumer_segment: normalizedMetadata.consumer_segment,
          popia_consent_at: normalizedMetadata.popia_consent_at,
          popia_consent_version: normalizedMetadata.popia_consent_version ?? 'May2026',
          marketing_consent: normalizedMetadata.marketing_consent,
        });
      } catch (profileSyncError) {
        console.warn('AuthContext: profile sync warning:', profileSyncError);
      }
    }
    const resolvedRole = await resolveUserRole(data.user ?? null);
    setRole(resolvedRole);
    if (error) throw error;
  };

  const signOut = async () => {
    // Optimistic local clear so role-switch login feels immediate.
    setUser(null);
    setRole(null);
    setLoading(false);

    await bestEffortGlobalSignOut();
    await clearServerSession();
    clearLocalAuthArtifacts();
  };

  return (
    <AuthContext.Provider value={{ user, role, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
